import { historicalFocusDate } from '@/lib/exchange-time'

/**
 * A panel is "history-anchored" — and so earns the yellow focus-date window
 * border — when it carries EITHER a historical focus date (its header calendar
 * or a history-row click) OR a historical drill anchor (a shift+click drill into
 * a PAST day writes `drillDate`).
 *
 * The two fields are deliberately separate: `focusDate` drives calendar /
 * data-fetch semantics, while `drillDate` is only the drill's display anchor.
 * Treating a historical `drillDate` as history mode is what makes "yellow
 * supersedes drill" fire — the window then carries both the drill (purple) and
 * focus-date (yellow) classes, and CSS source order lets yellow win. A drill
 * into TODAY (button / 'd' hotkey, or a today anchor) leaves the date
 * non-historical, so those correctly stay purple.
 */
export function isPanelHistoryAnchored(
  panel: { focusDate?: string | null; drillDate?: string | null } | undefined,
): boolean {
  return (
    historicalFocusDate(panel?.focusDate) != null || historicalFocusDate(panel?.drillDate) != null
  )
}

/**
 * History-page variant of the yellow-border rule. On History the baseline is the
 * RECORD being reviewed (e.g. 2026-01-03) — which is itself historical — not
 * today, so `isPanelHistoryAnchored` (date ≠ today) would light the border for
 * every selected record. Here the border means "the drill has navigated AWAY
 * from the record's date": yellow iff a `drillDate` is set AND differs from the
 * record baseline (`panel.focusDate`, the panel's own anchor, else the broadcast
 * `recordDate` from the clicked history row). Drilling exactly onto the record's
 * day stays neutral.
 *
 * With no record selected (`baseline == null`) it degrades to the dashboard
 * semantics (a historical drill lights it), so the helper is safe on any panel.
 */
export function isPanelDeviatedFromRecord(
  panel: { focusDate?: string | null; drillDate?: string | null } | undefined,
  recordDate: string | null,
): boolean {
  const drill = panel?.drillDate
  if (drill == null) return false
  const baseline = panel?.focusDate ?? recordDate
  if (baseline == null) return historicalFocusDate(drill) != null
  return drill !== baseline
}
